import { useState, useRef, useEffect } from "react";
import { Calendar, Clock, ChevronLeft, BookOpen } from "lucide-react";
import { useAuthContext } from "../Auth/AuthContext";
import config from "../../config";
import BotGuruQueuePage from "./loadingpage/BotGuruQueuePage";
import LoadTransitionPage from "./loadingpage/LoadTransitionPage";
import WaitingRoom from "./loadingpage/WaitingRoom";
import TransitionPage from "./TransitionPage";
import "./SessionDetailspage.css";
import './CompletedDetailsModal.css';

const OVERLAY_ID = "bg-assessment-iframe-overlay";
const IFRAME_CONTAINER_ID = "bg-assessment-iframe-container";

const SessionDetailsPage = ({
  sessionData,
  formatDate,
  getStatusBadgeClass,
  getStatusLabel,
  path
}) => {
  const { userObjId, timezone } = useAuthContext();
  // const session = sessionData.sessions?.[0];
  // const isCompleted = session?.status?.toLowerCase() === 'completed';
  // const isPending = session?.status?.toLowerCase() === 'in_progress' || session?.status?.toLowerCase() === 'pending';

  const [expandedSections, setExpandedSections] = useState({});
  const [showQueue, setShowQueue] = useState(false);
  const [queueError, setQueueError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [urlArrived, setUrlArrived] = useState(false);
  const [prepAssessment, setPrepAssessment] = useState({});

  const [showLaunchModal, setShowLaunchModal] = useState(false);
const [selectedSessionId, setSelectedSessionId] = useState(null);
// const [selectedMode, setSelectedMode] = useState(true); // true = prep, false = assessment
const [reportStatus, setReportStatus] = useState({});
const [hasConsented, setHasConsented] = useState(false);
const [assessmentError, setAssessmentError] = useState("");
const [feedback, setFeedback] = useState({
  show: false,
  sessionId: null,
  ratings: {},
  review: '',
  submitting: false,
  submitted: {}, // tracks per session
  error: '',
});

const updateFeedback = (updates) => setFeedback(prev => ({ ...prev, ...updates }));

const closeFeedbackModal = () => updateFeedback({ show: false, ratings: {}, review: '', error: '' });

const handleFeedbackSubmit = async () => {
  const hasAny = Object.keys(feedback.ratings).length > 0 || feedback.review.trim();
  if (!hasAny) { updateFeedback({ error: 'Please provide at least one rating or a review.' }); return; }

  updateFeedback({ submitting: true, error: '' });

  const payload = { feedbackType: 'reportFeedback', session_id: feedback.sessionId };
  Object.entries(feedback.ratings).forEach(([k, v]) => { if (v) payload[k] = v; });
  if (feedback.review.trim()) payload.detailedReview = feedback.review.trim();

  try {
    const res = await fetch(`${config.restAPI}/accounts/submitFeedback/`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      updateFeedback({ submitted: { ...feedback.submitted, [feedback.sessionId]: true } });
      setTimeout(closeFeedbackModal, 1500);
    } else {
      const data = await res.json();
      updateFeedback({ error: data.message || 'Failed to submit.' });
    }
  } catch { updateFeedback({ error: 'Network error. Please try again.' }); }
  finally { updateFeedback({ submitting: false }); }
};



  const firstRender = useRef(true);
  const prevIsLoading = useRef(false);
  const iframeOpenRequested = useRef(false);
  const sessionUrlRef = useRef(null);


  function closeInterviewIframe() {
    try { document.getElementById(IFRAME_CONTAINER_ID)?.remove(); } catch {}
    try { document.getElementById(OVERLAY_ID)?.remove(); } catch {}
    setIsLoading(false);
    setShowQueue(false);
    setShowTransition(false);
  }

  const handleJoinSession = () => {
    const url = sessionUrlRef.current;
    if (!url) return;
  
    setShowTransition(false);
  
    // Create iframe container
    const iframeContainer = document.createElement("div");
    iframeContainer.id = IFRAME_CONTAINER_ID;
    iframeContainer.style.position = "fixed";
    iframeContainer.style.top = "0";
    iframeContainer.style.left = "0";
    iframeContainer.style.width = "100%";
    iframeContainer.style.height = "100%";
    iframeContainer.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
    iframeContainer.style.zIndex = "9999";
  
    const iframe = document.createElement("iframe");
    iframe.src = url;
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.border = "none";
    iframe.allow = "camera; microphone; fullscreen";
  
    const closeButton = document.createElement("button");
    closeButton.innerText = "Close";
    closeButton.style.position = "absolute";
    closeButton.style.bottom = "10px";
    closeButton.style.left = "10px";
    closeButton.style.zIndex = "10000";
    closeButton.style.padding = "10px";
    closeButton.style.backgroundColor = "#be01f9";
    closeButton.style.color = "#fff";
    closeButton.style.border = "none";
    closeButton.style.cursor = "pointer";
    closeButton.style.borderRadius = "5px";
    closeButton.onclick = () => {
      closeInterviewIframe();
      window.location.assign("https://botguru.ai/landingPage/prepBot");
    };
  
    iframeContainer.appendChild(iframe);
    iframeContainer.appendChild(closeButton);
    document.body.appendChild(iframeContainer);
  };

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data === "operationComplete") {
        iframeOpenRequested.current = true;
        return;
      }
      if (e.data && typeof e.data === "object" && e.data.type === "BOTGURU_GOODBYE") {
        closeInterviewIframe();
        try {
          window.location.assign("/landingPage/assessmentBot");
        } catch {
          window.location.href = "/landingPage/assessmentBot";
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Show transition fallback after loader ends but before URL arrives
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (prevIsLoading.current && !isLoading && !showQueue && !urlArrived) {
      setShowTransition(true);
    }
    prevIsLoading.current = isLoading;
  }, [isLoading, showQueue, urlArrived]);


  const toggleSection = (sessionIdx, moduleIdx) => {
    const key = `${sessionIdx}-${moduleIdx}`;
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  // Calculate how many topics each module should show

  const getVisibleCounts = (modules, sessionIdx) => {
    if (!modules) return {};
    const counts = {};
    const DEFAULT = 3;

    let maxShowing = DEFAULT;
    modules.forEach((mod, i) => {
      const key = `${sessionIdx}-${i}`;
      const showing = expandedSections[key] ? mod.topics.length : DEFAULT;
      if (showing > maxShowing) maxShowing = showing;
      counts[i] = showing;
    });

    modules.forEach((mod, i) => {
      const key = `${sessionIdx}-${i}`;
      if (!expandedSections[key]) {
        counts[i] = Math.min(maxShowing, mod.topics.length);
      }
    });

    return counts;
  };

  const [reportUrl, setReportUrl] = useState("");
const [isViewModalOpen, setIsViewModalOpen] = useState(false);

useEffect(() => {
  const checkReports = async () => {
    const completedSessions = sessionData.sessions?.filter(
      (s) => s.status?.toLowerCase() === "completed"
    );
    if (!completedSessions?.length) return;

    for (const session of completedSessions) {
      try {
        const response = await fetch(`${config.restAPI}/assessments/getPrepAssessmentReport/`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_obj_id: userObjId,
            session_id: session.assessment_session_id,
          }),
        });
        const data = await response.json();
        setReportStatus((prev) => ({
          ...prev,
          [session.assessment_session_id]: data,
        }));
      } catch {
        setReportStatus((prev) => ({
          ...prev,
          [session.assessment_session_id]: { report_exists: false },
        }));
      }
    }
  };
  checkReports();
}, [sessionData]);

const handleReport = (type, sessionId) => {
  const data = reportStatus[sessionId];
  if (!data?.report_exists) return;

  if (type === "view" && data?.html_url_view) {
    setReportUrl(data.html_url_view);
    setIsViewModalOpen(true);
  } else if (type === "download" && data?.html_url_download) {
    window.open(data.html_url_download);
  }
};

const closeViewModal = () => {
  setIsViewModalOpen(false);
  setReportUrl("");
};

  const handleStartAssessment = (assessmentSessionId,isPrepValue) => {
    setUrlArrived(false);
    setShowTransition(false);
    setShowQueue(true);
    setQueueError(false);


    const websocketUrl = "wss://ycbrz3lmnc.execute-api.us-west-2.amazonaws.com/production/";
    const websocket = new WebSocket(websocketUrl);
    let newUrl = null;

    websocket.onopen = () => {
      websocket.send(JSON.stringify({ action: "SendMessage", message: "get data" }));
    };

    websocket.onmessage = async (event) => {
      try {
        if (!event.data || event.data.trim() === "") return;
        const data = JSON.parse(event.data);

        if (data === "go") {
          setShowQueue(false);
          setShowTransition(false);
          setIsLoading(true);
        } else if (data === "no") {
          setQueueError(true);
        }

        if (data.connectionid) {
          const payload = {
            session_id:assessmentSessionId,
            connectionId: data.connectionid,
            user_obj_id: userObjId,
            assessment_path_id: path.assessment_path_id,
            local_time: new Date().toLocaleString(),
            prep_assessment: isPrepValue,
            timezone: timezone,
            consent: true,
          };

          try {
            const response = await fetch(`${config.restAPI}/assessments/startPrepAssessment/`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            });
            const responseData = await response.json();
             if (responseData.error) {
    setShowQueue(false);
    setIsLoading(false);
    setShowTransition(false);
    websocket.close();
    setAssessmentError(responseData.error);
    return;
  }
  if (!response.ok) throw new Error(`Failed. Status: ${response.status}`);

            console.log("Assessment started:", responseData);
          } catch (apiError) {
            console.error("Error starting assessment:", apiError);
            setShowQueue(false);
            setIsLoading(false);
            setShowTransition(false);
          }
        } else if (data.url) {
          console.log(data.url);
          setUrlArrived(true);
          setShowTransition(false);
          newUrl = data.url;
          sessionUrlRef.current = data.url;


          const openIframe = () => {
            const iframeContainer = document.createElement("div");
            iframeContainer.id = IFRAME_CONTAINER_ID;
            iframeContainer.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;";

            const iframe = document.createElement("iframe");
            iframe.src = newUrl;
            iframe.style.cssText = "width:100%;height:100%;border:none;";
            iframe.allow = "camera; microphone; fullscreen";

            const closeButton = document.createElement("button");
            closeButton.innerText = "Close";
            closeButton.style.cssText = "position:absolute;bottom:10px;left:10px;z-index:10000;padding:10px;background:#be01f9;color:#fff;border:none;cursor:pointer;border-radius:5px;";
            closeButton.onclick = () => {
              closeInterviewIframe();
              window.location.assign("/landingPage/assessmentBot");
            };

            iframeContainer.appendChild(iframe);
            iframeContainer.appendChild(closeButton);
            document.body.appendChild(iframeContainer);
          };

          if (iframeOpenRequested.current) {
            // Loader already done, show "Let's Start" then iframe
            const overlay = document.createElement("div");
            overlay.id = OVERLAY_ID;
            overlay.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.6);z-index:9998;";

            const launchButton = document.createElement("button");
            launchButton.innerText = "Let's Start";
            launchButton.style.cssText = "position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);padding:15px 25px;font-size:18px;background:#be01f9;color:#fff;border:none;border-radius:8px;cursor:pointer;z-index:9999;";
            launchButton.onclick = () => {
              document.body.removeChild(launchButton);
              document.body.removeChild(overlay);
              setTimeout(openIframe, 450);
            };

            document.body.appendChild(overlay);
            document.body.appendChild(launchButton);
          } else {
            // Wait for operationComplete from loader iframe
            const handleOpComplete = (e) => {
              if (e.data === "operationComplete") {
                iframeOpenRequested.current = true;
                openIframe();
                window.removeEventListener("message", handleOpComplete);
              }
            };
            window.addEventListener("message", handleOpComplete);
          }
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    websocket.onerror = (error) => console.error("WebSocket error:", error);
    websocket.onclose = () => console.log("WebSocket closed.");
  };

  // Render queue/loading/transition states
  if (showQueue) {
    return (
      <BotGuruQueuePage
        error={queueError}
        onBack={() => { setShowQueue(false); setQueueError(false); }}
      />
    );
  }

  // if (isLoading) {
  //   return <LoadTransitionPage url="https://botguru.ai:5174/" setIsLoading={setIsLoading} />;
  // }

  {isLoading && (
    <WaitingRoom
      urlArrived={urlArrived}
      onJoinSession={handleJoinSession}
    />
  )}

  if (showTransition) {
    return <TransitionPage setShowTransitionPage={setShowTransition} cancel={urlArrived} />;
  }

  return (
    <>
    {assessmentError && (
      <div className="assessment-error-banner">
        <span>{assessmentError}</span>
        <button onClick={() => setAssessmentError("")}>&times;</button>
      </div>
    )}

    <div className="session-detail-container-list">
      {sessionData.sessions?.map((session, sessionIdx) => {
        const isCompleted = session?.status?.toLowerCase() === "completed";
        const isPending =
          session?.status?.toLowerCase() === "in_progress" ||
          session?.status?.toLowerCase() === "pending";
        const visibleCounts = getVisibleCounts(session.modules, sessionIdx);

        return (
          <div key={sessionIdx} className="session-detail-card">
            <div className="session-detail-header">
              <div className="session-detail-title-row">
                <h2 className="session-detail-title">
                  {session.session_type.toUpperCase()}
                </h2>
                <span
                  className={`status-badge ${getStatusBadgeClass(
                    session.status
                  )}`}
                >
                  {getStatusLabel(session.status)}
                </span>
              </div>
              <div className="session-detail-meta">
                <span className="session-detail-date">
                  <Calendar size={14} />
                  {formatDate(session.created_at)}
                </span>
                <span className="session-detail-duration">
                  <Clock size={14} />1 hr
                </span>
              </div>
            </div>

            <div className="session-sections-grid">
              {session.modules?.map((module, idx) => {
                const key = `${sessionIdx}-${idx}`;
                const total = module.topics?.length || 0;
                const visible = visibleCounts[idx] ?? 3;
                const hiddenCount = total - visible;

                return (
                  <div key={idx} className="session-section">
                    <span className="session-section-title">
                      <BookOpen size={25} />
                      {module.title}
                    </span>
                    <ul className="session-section-topics">
                      {module.topics?.slice(0, visible).map((topic, i) => (
                        <li key={i} className="session-topic-item">
                          {topic}
                        </li>
                      ))}
                    </ul>
                    {total > 3 &&
                      (hiddenCount > 0 || expandedSections[key]) && (
                        <button
                          className="view-more-btn"
                          onClick={() => toggleSection(sessionIdx, idx)}
                        >
                          <ChevronLeft
                            size={14}
                            className={
                              expandedSections[key]
                                ? "rotate-down"
                                : "rotate-right"
                            }
                          />
                          {expandedSections[key]
                            ? "View Less"
                            : `View ${hiddenCount} More`}
                        </button>
                      )}
                  </div>
                );
              })}
            </div>

            <div className="session-detail-footer">
    {isPending && (
        <button
            className="session-action-btn start-btn"
            onClick={(e) => {
                e.stopPropagation();
                setSelectedSessionId(session.assessment_session_id);
                // setSelectedMode(true);
                setShowLaunchModal(true);
            }}
        >
            <i className="fas fa-play"></i>Start Assessment
        </button>
    )}
{isCompleted && (
  <>
  <button
  className={`session-action-btn report-btn ${feedback.submitted[session.assessment_session_id] ? 'btn-disabled' : ''}`}
  disabled={feedback.submitted[session.assessment_session_id]}
  onClick={(e) => {
    e.stopPropagation();
    updateFeedback({ show: true, sessionId: session.assessment_session_id });
  }}
>
  <i className={`fas ${feedback.submitted[session.assessment_session_id] ? 'fa-check' : 'fa-comment-dots'}`}></i>
  {feedback.submitted[session.assessment_session_id] ? 'Feedback Sent' : 'Submit Feedback'}
</button>

    <div className="report-btn-wrapper">
      <button
        className={`session-action-btn report-btn ${!reportStatus[session.assessment_session_id]?.report_exists ? 'btn-disabled' : ''}`}
        disabled={!reportStatus[session.assessment_session_id]?.report_exists}
        onClick={(e) => {
          e.stopPropagation();
          handleReport("view", session.assessment_session_id);
        }}
      >
        <i className="fas fa-eye"></i>View Report
      </button>
      {!reportStatus[session.assessment_session_id]?.report_exists && (
        <span className="report-tooltip">Report is not yet ready</span>
      )}
    </div>
    <div className="report-btn-wrapper">
      <button
        className={`session-action-btn report-btn ${!reportStatus[session.assessment_session_id]?.report_exists ? 'btn-disabled' : ''}`}
        disabled={!reportStatus[session.assessment_session_id]?.report_exists}
        onClick={(e) => {
          e.stopPropagation();
          handleReport("download", session.assessment_session_id);
        }}
      >
        <i className="fas fa-download"></i>Download Report
      </button>
      {!reportStatus[session.assessment_session_id]?.report_exists && (
        <span className="report-tooltip">Report is not yet ready</span>
      )}
    </div>
  </>
)}
</div>
          </div>
        );
      })}
    </div>
    {showLaunchModal && (
    <div className="launch-modal-overlay" onClick={() => setShowLaunchModal(false)}>
        <div className="launch-modal" onClick={(e) => e.stopPropagation()}>
            <button className="launch-modal-close" onClick={() => { setShowLaunchModal(false); setHasConsented(false); }}>
                &times;
            </button>
            <h2 className="launch-modal-title">Botguru Disclosure & Consent</h2>
<p className="launch-modal-subtitle">Please read and consent before proceeding</p>

<div className="launch-consent-body">
    <p>You are about to participate in an interview conducted by the <span className="launch-modal-header">BOTGURU</span>, an artificial intelligence system.</p>
    <p>Before proceeding, please note:</p>
    <ul>
        <li>The interviewer is an AI system, not a human.</li>
        <li>Your video, audio, and screen activity (if required) will be recorded.</li>
        <li>Limited location metadata (e.g., IP address) will be collected for security and fraud prevention.</li>
        <li>A transcript and AI-generated evaluation report will be created.</li>
        <li>All materials will be reviewed by authorized human personnel.</li>
        <li>Prep assessment session recording will be securely stored for up to 30 days, unless otherwise required by law.</li>
    </ul>
    <p>By clicking "I Agree" and proceeding, you acknowledge and consent to the above.</p>
</div>

<label className="launch-consent-checkbox">
    <input
        type="checkbox"
        checked={hasConsented}
        onChange={(e) => setHasConsented(e.target.checked)}
    />
   <span>I Agree and Consent to Proceed <span style={{ color: 'red' }}>*</span></span>
</label>

<button
    className="launch-modal-btn"
    disabled={!hasConsented}
    title={!hasConsented ? "Please accept the disclosure and consent" : ""}
    onClick={() => {
        setShowLaunchModal(false);
        setHasConsented(false);
        handleStartAssessment(selectedSessionId, true);
    }}
>
    <i className="fas fa-rocket"></i>
    Launch Assessment
</button>
            {/* <h2 className="launch-modal-title">Choose Assessment Mode</h2>
            <p className="launch-modal-subtitle">Select how you'd like to take this assessment</p>

            <div className="launch-mode-cards">
                <div
                    className={`launch-mode-card ${selectedMode ? "selected" : ""}`}
                    onClick={() => setSelectedMode(true)}
                >
                    <div className="launch-mode-radio">
                        {selectedMode && <div className="launch-mode-radio-dot" />}
                    </div>
                    <div className="launch-mode-icon">
                        <i className="fas fa-graduation-cap"></i>
                    </div>
                    <div className="launch-mode-info">
                        <span className="launch-mode-title">Prep Assessment</span>
                        <span className="launch-mode-desc">Practice mode with guidance and hints to help you learn</span>
                    </div>
                </div>
                <div
                    className={`launch-mode-card ${!selectedMode ? "selected" : ""}`}
                    onClick={() => setSelectedMode(false)}
                >
                    <div className="launch-mode-radio">
                        {!selectedMode && <div className="launch-mode-radio-dot" />}
                    </div>
                    <div className="launch-mode-icon">
                        <i className="fas fa-clipboard-check"></i>
                    </div>
                    <div className="launch-mode-info">
                        <span className="launch-mode-title">Assessment</span>
                        <span className="launch-mode-desc">Timed evaluation to test your knowledge and skills</span>
                    </div>
                </div>
            </div> */}

            {/* <button
                className="launch-modal-btn"
                onClick={() => {
                    setShowLaunchModal(false);
                    setHasConsented(false);
                    handleStartAssessment(selectedSessionId, true);
                }}
            >
                <i className="fas fa-rocket"></i>
                {selectedMode ? "Launch Prep Assessment" : "Launch Assessment"}
            </button> */}
            
        </div>
    </div>
)}

{isViewModalOpen && (
  <div className="report-view-modal-overlay">
    <div className="report-view-modal-content">
      <button className="report-view-close-button" onClick={closeViewModal}>
        &times;
      </button>
      <iframe
        src={reportUrl}
        frameBorder="0"
      />
    </div>
  </div>
)}

{feedback.show && (
  <div className="launch-modal-overlay" onClick={closeFeedbackModal}>
    <div className="launch-modal sfb-modal" onClick={(e) => e.stopPropagation()}>
      <button className="launch-modal-close" onClick={closeFeedbackModal}>&times;</button>

      {feedback.submitted[feedback.sessionId] ? (
        <div className="sfb-success">
          <i className="fas fa-check-circle sfb-success-icon"></i>
          <h2 className="launch-modal-title">Thank You!</h2>
          <p className="launch-modal-subtitle">Your feedback has been submitted successfully.</p>
        </div>
      ) : (
        <>
          <h2 className="launch-modal-title">Evaluation Report Feedback</h2>
          {/* <p className="launch-modal-subtitle">Help us improve your assessment experience</p> */}

          <div className="sfb-questions">
            {[
              { key: 'technicalEvaluationRating', text: 'How well do you think BotGuru evaluated your technical knowledge and problem-solving ability?' },
              { key: 'reportFeedbackAgreementRating', text: 'How much do you agree with the evaluation report and feedback provided by BotGuru on your performance?' },
            ].map(({ key, text }) => (
              <div key={key} className="sfb-question">
                <span className="sfb-question-text">{text}</span>
                <div className="sfb-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`sfb-star-btn ${star <= (feedback.ratings[key] || 0) ? 'sfb-star-active' : 'sfb-star-inactive'}`}
                      onClick={() => updateFeedback({
                        ratings: { ...feedback.ratings, [key]: star.toString() === feedback.ratings[key] ? undefined : star.toString() }
                      })}
                    >
                      <i className={`fa${star <= (feedback.ratings[key] || 0) ? 's' : 'r'} fa-star`}></i>
                    </button>
                  ))}
                  {feedback.ratings[key] && <span className="sfb-star-label">{feedback.ratings[key]}/5</span>}
                </div>
              </div>
            ))}

            <div>
              <span className="sfb-review-label">Detailed Review</span>
              <textarea
                className="sfb-textarea"
                value={feedback.review}
                onChange={(e) => updateFeedback({ review: e.target.value })}
                placeholder="Share your thoughts about this evaluation report..."
                maxLength={2000}
              />
              <div className="sfb-counter">{feedback.review.length}/2000</div>
            </div>
          </div>

          {feedback.error && <div className="sfb-error">{feedback.error}</div>}

          <button
            className={`launch-modal-btn ${feedback.submitting || (!Object.keys(feedback.ratings).filter(k => feedback.ratings[k]).length && !feedback.review.trim()) ? 'sfb-submit-disabled' : ''}`}
            disabled={feedback.submitting || (!Object.keys(feedback.ratings).filter(k => feedback.ratings[k]).length && !feedback.review.trim())}
            onClick={handleFeedbackSubmit}
          >
            <i className={`fas ${feedback.submitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
            {feedback.submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </>
      )}
    </div>
  </div>
)}

    </>
  );
};

export default SessionDetailsPage;